import React, { useEffect, useState } from 'react';
import { Cpu, RefreshCw } from 'lucide-react';
import { aiModelsApi, apiErrorMessage } from '../../services/aiModelsApi';
import './DashboardFusionPage.css';
import './AIModelsPage.css';

const keyOf = ({ provider, model }) => JSON.stringify([provider, model]);
const providerName = { vertex: 'Gemini / Vertex AI', openai: 'Foundry / OpenAI', anthropic: 'Foundry / Anthropic' };

function ModelCard({ usage, catalog, busy, onSave }) {
  const currentKey = keyOf(usage);
  const [selected, setSelected] = useState(currentKey);
  const [confirm, setConfirm] = useState(null);
  const choices = catalog?.models || [];
  const listed = choices.some((item) => keyOf(item) === selected);
  const changed = selected !== currentKey;
  return (
    <article className="ai-model-card" aria-label={usage.label}>
      <h2>{usage.label}</h2>
      <p>Actual: <strong>{usage.model}</strong> · {providerName[usage.provider] || usage.provider}</p>
      <p>Origen: {usage.source === 'portal' ? 'OpsGovernance' : 'Variables de HealthCenter'}</p>
      <label htmlFor={`model-${usage.id}`}>Modelo o deployment</label>
      <select id={`model-${usage.id}`} value={selected} disabled={busy || !catalog || Boolean(catalog.error)}
        onChange={(event) => { setSelected(event.target.value); setConfirm(null); }}>
        {!listed && <option value={selected} disabled>{usage.model} (fuera del catálogo consultado)</option>}
        {choices.map((item) => (
          <option key={keyOf(item)} value={keyOf(item)}>{providerName[item.provider]} · {item.label}</option>
        ))}
      </select>
      {!catalog && <p role="status">Consultando catálogo...</p>}
      {catalog?.error && <p role="alert">{catalog.error}</p>}
      {catalog && !catalog.error && !choices.length && <p role="alert">La conexión no devolvió modelos compatibles.</p>}
      <div className="ai-model-actions">
        <button disabled={busy || !changed || !listed || Boolean(catalog?.error)} onClick={() => setConfirm('save')}>
          Validar y guardar
        </button>
        <button disabled={busy || usage.source !== 'portal'} onClick={() => setConfirm('reset')}>
          Restaurar configuración del servicio
        </button>
      </div>
      {confirm && (
        <div className="ai-model-confirm" role="group" aria-label={`Confirmar cambio de ${usage.label}`}>
          <p>Se probará {confirm === 'reset' ? 'el modelo definido en las variables del servicio' : JSON.parse(selected)[1]} con
            datos sintéticos. Puede generar un cargo pequeño. Si valida, se aplicará a nuevas ejecuciones.</p>
          <button disabled={busy} onClick={() => {
            const [provider, model] = JSON.parse(selected);
            onSave(usage.id, confirm === 'reset' ? null : { provider, model });
            setConfirm(null);
          }}>Confirmar cambio</button>
          <button disabled={busy} onClick={() => setConfirm(null)}>Cancelar</button>
        </div>
      )}
    </article>
  );
}

export default function AIModelsPage() {
  const [config, setConfig] = useState(null);
  const [catalogs, setCatalogs] = useState({});
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setMessage('');
    setCatalogs({});
    aiModelsApi.configuration().then((data) => {
      if (active) setConfig(data);
    }).catch((failure) => {
      if (active) { setConfig(null); setError(apiErrorMessage(failure, 'No se pudo cargar la configuración IA.')); }
    }).finally(() => { if (active) setLoading(false); });
    ['vertex', 'foundry'].forEach((adapter) => {
      aiModelsApi.catalog(adapter).then((data) => {
        if (active) setCatalogs((previous) => ({ ...previous, [adapter]: data }));
      }).catch((failure) => {
        if (active) setCatalogs((previous) => ({
          ...previous, [adapter]: { error: apiErrorMessage(failure, 'No se pudo consultar el catálogo del proveedor.') },
        }));
      });
    });
    return () => { active = false; };
  }, [version]);
  const save = async (usage, selection) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const data = await aiModelsApi.save(usage, selection, config.revision);
      setConfig(data);
      setMessage('Modelo validado y guardado. Se aplicará a nuevos clientes de IA; las llamadas iniciadas no cambian.');
    } catch (failure) {
      setError(`${apiErrorMessage(failure, 'No se pudo validar o guardar el modelo.')} Recarga para verificar el estado antes de reintentar.`);
    } finally { setBusy(false); }
  };
  return (
    <main className="innovation-fusion ai-models-page">
      <header className="ai-model-heading">
        <div><p className="ai-model-eyebrow">Innovation</p><h1><Cpu size={26} />Modelos de IA</h1><p>HealthCenter · Selección por función</p></div>
        <button disabled={loading || busy} onClick={() => setVersion((value) => value + 1)}><RefreshCw size={16} />Actualizar configuración y catálogos</button>
      </header>
      <section className="ai-model-info" aria-label="Acerca de los modelos">
      <p>Selecciona el modelo que usará cada función de HealthCenter. Las credenciales y conexiones permanecen en el servicio.</p>
      <details><summary>Catálogos y validación de cambios</summary>
      <p>Vertex muestra modelos Gemini de texto publicados por Google; Foundry, deployments compatibles del proyecto.
        El catálogo no garantiza cuota, disponibilidad regional ni acceso: se comprueban con una llamada breve al guardar.
        La calidad del reporte debe verificarse después del cambio. Esta primera etapa no modifica otros proyectos.</p>
      </details></section>
      {loading && <p role="status">Cargando configuración...</p>}
      {error && <p role="alert" className="ai-model-error">{error}</p>}
      {!loading && !config && error && <section className="ai-model-setup" aria-label="Conexión con HealthCenter">
        <h2>Conexión con HealthCenter pendiente</h2>
        <p>La gestión de modelos necesita una conexión de servicio; iniciar sesión en OpsGovernance no la configura.</p>
        <ol>
          <li>En HealthCenter: habilitar <code>AI_MODEL_CONFIG_ENABLED=true</code> en web y Celery, y configurar <code>AI_MODEL_CONFIG_TOKEN</code> en web.</li>
          <li>En el backend de Ops: configurar el mismo <code>AI_MODEL_CONFIG_TOKEN</code> (mínimo 32 caracteres) y <code>HEALTHCENTER_AI_CONFIG_URL=https://healthcenter.kloud-pilot.com</code>.</li>
          <li>Desplegar ambos servicios con esas variables y actualizar esta página. Si persiste el error, revisar conectividad y autenticación entre servicios.</li>
        </ol>
        <p>El token debe guardarse como variable protegida y enmascarada de CI/CD. No lo compartas en el chat ni lo ingreses en esta página.</p>
      </section>}
      {message && <p role="status">{message}</p>}
      {!loading && config && <>
        <p>Revisión {config.revision} · Ubicación Vertex: {config.location}</p>
        {!config.foundry_enabled && <p role="status">La generación Foundry está deshabilitada en el servicio. Seleccionar un deployment no la habilita.</p>}
        {busy && <p role="status">Consultando catálogo, validando y guardando; espera la confirmación...</p>}
        <div className="ai-model-grid">
          {config.usages.map((usage) => (
            <ModelCard key={`${usage.id}:${keyOf(usage)}:${usage.source}`} usage={usage} catalog={catalogs[usage.adapter]} busy={busy}
              onSave={save} />
          ))}
        </div>
        <section className="ai-model-history"><h2>Últimos cambios</h2>
        <div className="ai-model-history-scroll">
          <table>
            <thead><tr><th>Revisión</th><th>Fecha</th><th>Usuario OPS</th><th>Función</th><th>Anterior</th><th>Nuevo</th></tr></thead>
            <tbody>{config.history.map((entry) => (
              <tr key={entry.revision}>
                <td>{entry.revision}</td><td>{new Date(entry.created_at).toLocaleString('es-MX')}</td>
                <td>{entry.actor}</td><td>{config.usages.find((usage) => usage.id === entry.usage)?.label || entry.usage}</td>
                <td>{entry.before.provider} / {entry.before.model}</td><td>{entry.after.provider} / {entry.after.model}</td>
              </tr>
            ))}</tbody>
          </table>
          {!config.history.length && <p>No hay cambios publicados desde el portal.</p>}
        </div>
        </section>
      </>}
    </main>
  );
}
